/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 */
import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiInstance } from '../../Apis/Axios';
import { URL } from '../../Apis/Constants';
import { BUILDING_SERVICE } from '../EndPoints';

// List all the buildings
export const GetAllBuildings = createAsyncThunk(
    'buildingSection/getAllBuildings',
    async () => {
        const response = await apiInstance
            .get(BUILDING_SERVICE.getAllBuildings)
            .then((response) => {
                return response;
            })
            .catch((error) => {
                return error.response;
            });
        const { data, status, code } = response;
        return { data, status, code };
    },
);

// Get a building by its id without a session, the external registration flow
// runs it before the resident is authenticated
export const GetPublicBuildingById = createAsyncThunk(
    'buildingSection/getPublicBuildingById',
    async (id) => {
        const response = await axios
            .get(`${URL}${BUILDING_SERVICE.getPublicBuildingById}${id}`)
            .then((response) => {
                return response;
            })
            .catch((error) => {
                return error.response;
            });
        const { data, status, code } = response;
        return { data, status, code };
    },
);

// Create a building
export const CreateBuilding = createAsyncThunk(
    'buildingSection/createBuilding',
    async (body) => {
        const response = await apiInstance
            .post(BUILDING_SERVICE.createBuilding, body)
            .then((response) => {
                return response;
            })
            .catch((error) => {
                return error.response;
            });
        const { data, status, code } = response;
        return { data, status, code };
    },
);

// Update a building
export const UpdateBuilding = createAsyncThunk(
    'buildingSection/updateBuilding',
    async (body) => {
        const response = await apiInstance
            .put(BUILDING_SERVICE.updateBuilding, body)
            .then((response) => {
                return response;
            })
            .catch((error) => {
                return error.response;
            });
        const { data, status, code } = response;
        return { data, status, code };
    },
);

// Add residents to the white list of a building
export const CreateResidentWhiteList = createAsyncThunk(
    'buildingSection/createResidentWhiteList',
    async (body) => {
        const response = await apiInstance
            .post(BUILDING_SERVICE.createResidentWhiteList, body)
            .then((response) => {
                return response;
            })
            .catch((error) => {
                return error.response;
            });
        const { data, status, code } = response;
        return { data, status, code };
    },
);

// Upload the white list template of a building (Automatic tab of the white list modal).
// Body: FormData with the buildingId and the xlsx file.
export const UploadResidentWhiteList = createAsyncThunk(
    'buildingSection/uploadResidentWhiteList',
    async (body) => {
        const response = await apiInstance
            .post(BUILDING_SERVICE.uploadResidentWhiteList, body)
            .then((response) => {
                return response;
            })
            .catch((error) => {
                return error.response;
            });
        const { data, status, code } = response;
        return { data, status, code };
    },
);

// Users assigned to a building (Assigned tab of the update building modal).
// Body: { pagination:{page,size}, model, resourceId } (key may be null).
export const GetBuildingUserAssigned = createAsyncThunk(
    'buildingSection/getBuildingUserAssigned',
    async (body) => {
        const response = await apiInstance
            .post(BUILDING_SERVICE.getBuildingUserAssigned, body)
            .then((response) => {
                return response;
            })
            .catch((error) => {
                return error.response;
            });
        const { data, status, code } = response;
        return { data, status, code };
    },
);

// Users not assigned to a building (Add tab of the update building modal).
// Body: { pagination:{page,size}, model, modelId } (key may be null).
export const GetBuildingUserNotAssigned = createAsyncThunk(
    'buildingSection/getBuildingUserNotAssigned',
    async (body) => {
        const response = await apiInstance
            .post(BUILDING_SERVICE.getBuildingUserNotAssigned, body)
            .then((response) => {
                return response;
            })
            .catch((error) => {
                return error.response;
            });
        const { data, status, code } = response;
        return { data, status, code };
    },
);

// Assign users to a building. Body: { id: buildingId, idList: [userId] }.
// The service filters the ids: repeated, already assigned and unknown ones are ignored.
export const CreateBuildingUsers = createAsyncThunk(
    'buildingSection/createBuildingUsers',
    async (body) => {
        const response = await apiInstance
            .post(BUILDING_SERVICE.createBuildingUsers, body)
            .then((response) => {
                return response;
            })
            .catch((error) => {
                return error.response;
            });
        const { data, status, code } = response;
        return { data, status, code };
    },
);

// Register a resident of an apartment (household member of the logged resident).
// Body: { buildingApartmentId, name, lastName, email, residentId, residentType, relationshipType }.
// The residentId is the resident that registers the member, it can not be null.
export const CreateResident = createAsyncThunk(
    'buildingSection/createResident',
    async (body) => {
        const response = await apiInstance
            .post(BUILDING_SERVICE.createResident, body)
            .then((response) => {
                return response;
            })
            .catch((error) => {
                return error.response;
            });
        const { data, status, code } = response;
        return { data, status, code };
    },
);

// Residents of one apartment of a building (details modal of the units tab).
// The response is the list of building_apartment_resident rows of the apartment.
export const GetBuildingApartmentById = createAsyncThunk(
    'buildingSection/getBuildingApartmentById',
    async (id) => {
        const response = await apiInstance
            .get(`${BUILDING_SERVICE.getBuildingApartmentById}${id}`)
            .then((response) => {
                return response;
            })
            .catch((error) => {
                return error.response;
            });
        const { data, status, code } = response;
        return { data, status, code };
    },
);

// Validates that the resident belongs to the building and to the apartment they
// captured (identify you step of the external building registration).
// Body: { buildingId, name, lastName, email, apartment }.
// The resident has no session yet, the call goes out without the session token.
export const PublicValidateResidentOwner = createAsyncThunk(
    'buildingSection/publicValidateResidentOwner',
    async (body) => {
        const response = await axios
            .post(`${URL}${BUILDING_SERVICE.publicValidateResidentOwner}`, body)
            .then((response) => {
                return response;
            })
            .catch((error) => {
                return error.response;
            });
        const { data, status, code } = response;
        return { data, status, code };
    },
);

// Detail of one apartment without a session (information card of the my
// apartment details view). The response carries the building, the apartment,
// its coordinates and the contact data of the owner of the unit.
export const GetPublicBuildingApartmentDetails = createAsyncThunk(
    'buildingSection/getPublicBuildingApartmentDetails',
    async (id) => {
        const response = await axios
            .get(`${URL}${BUILDING_SERVICE.getPublicBuildingApartmentDetails}${id}`)
            .then((response) => {
                return response;
            })
            .catch((error) => {
                return error.response;
            });
        const { data, status, code } = response;
        return { data, status, code };
    },
);
